/**
 * 科技蓝模板 - 登录页
 * 
 * 设计理念：全屏左右分栏
 * - 左侧(42%)：淡蓝渐变色块+抽象几何插图区
 * - 右侧(58%)：白色表单区
 * - 同模板注册页为多步骤向导，两者排版完全不同
 */

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteConfig, loading: configLoading } = useSiteConfig();
  const from = (location.state as { from?: string })?.from || "/";
  const siteLogo = siteConfig?.logo || "";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!username.trim()) { setError("请输入用户名"); return; }
    if (!password.trim()) { setError("请输入密码"); return; }
    const result = await login(username, password);
    if (result.success) { navigate(from, { replace: true }); }
    else { setError(result.message || "登录失败"); }
  };

  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* 左侧插图区 */}
      <div className="hidden lg:flex lg:w-[42%] xl:w-[40%] relative overflow-hidden bg-gradient-to-br from-cyan-50 via-sky-50 to-white">
        {/* 抽象几何装饰 */}
        <div className="absolute top-12 right-12 w-48 h-48 bg-cyan-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-8 w-64 h-64 bg-sky-300/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/4 w-32 h-32 border-2 border-cyan-200/60 rounded-2xl rotate-12" />
        <div className="absolute top-1/2 right-1/4 w-24 h-24 border-2 border-sky-200/60 rounded-full" />
        <div className="absolute bottom-1/4 left-1/3 w-20 h-20 bg-cyan-100/50 rounded-lg -rotate-6" />

        <div className="relative z-10 flex flex-col justify-center w-full p-12 xl:p-16">
          <Link to="/" className="flex items-center gap-3 mb-16">
            {siteLogo && <img src={siteLogo} alt={siteDisplayName} className="h-11 w-11 object-contain rounded-xl bg-white shadow-sm p-1.5" />}
            {siteDisplayName && <span className="text-2xl font-bold text-gray-800">{siteDisplayName}</span>}
          </Link>

          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-800 leading-tight mb-4">
              欢迎回来<br /><span className="text-cyan-600">继续探索更多可能</span>
            </h2>
            <p className="text-gray-500 leading-relaxed max-w-xs">
              登录您的账户，发现更多商业机会，管理您的信息发布。
            </p>
            <div className="mt-10 space-y-4">
              {[
                { icon: "ri-database-2-line", text: "海量商业数据，精准分类检索" },
                { icon: "ri-bar-chart-2-line", text: "数据可视化分析，洞悉趋势" },
                { icon: "ri-time-line", text: "实时更新，信息始终最新" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 text-gray-600">
                  <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center"><i className={`${item.icon} text-cyan-600`} /></div>
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {siteDisplayName && <p className="mt-auto pt-16 text-xs text-gray-400">&copy; {new Date().getFullYear()} {siteDisplayName}</p>}
        </div>
      </div>

      {/* 右侧表单区 */}
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-12 sm:px-12 lg:px-16 xl:px-24">
        <div className="w-full max-w-[420px]">
          {/* 移动端Logo */}
          <div className="lg:hidden mb-10">
            <Link to="/" className="flex items-center gap-2.5">
              {siteLogo && <img src={siteLogo} alt={siteDisplayName} className="h-10 w-10 object-contain" />}
              {siteDisplayName && <span className="text-xl font-bold text-gray-800">{siteDisplayName}</span>}
            </Link>
          </div>

          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-cyan-600 transition-colors mb-8">
            <i className="ri-arrow-left-line" />返回首页
          </Link>

          <h1 className="text-3xl font-bold text-gray-900 mb-1">登录账户</h1>
          <p className="text-gray-500 text-sm mb-8">输入您的账户信息以继续</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">用户名</label>
              <div className="relative">
                <i className="ri-user-3-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="请输入用户名"
                  className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">密码</label>
              <div className="relative">
                <i className="ri-lock-2-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="请输入密码"
                  className="w-full pl-11 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent focus:bg-white transition-all" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-gray-500 cursor-pointer"><input type="checkbox" className="w-4 h-4 rounded accent-cyan-500" />记住我</label>
              <Link to="/forgot-password" className="text-cyan-600 hover:text-cyan-700 font-medium">忘记密码？</Link>
            </div>
            {error && <div className="p-4 bg-cyan-50 text-cyan-700 text-sm rounded-xl flex items-center gap-3 border border-cyan-100"><i className="ri-error-warning-line" />{error}</div>}
            <button type="submit" disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-semibold rounded-xl hover:from-cyan-700 hover:to-teal-700 transition-all disabled:opacity-50 shadow-lg shadow-cyan-500/25 text-base">
              {isLoading ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin" />登录中...</span> : "登  录"}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">还没有账户？<Link to="/register" className="text-cyan-600 font-semibold hover:text-cyan-700 underline underline-offset-2">立即注册</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
